/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import { store } from 'redux/store';
import { FC, ReactNode, createContext, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';

import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';
import { getAppDomainHost } from 'utils/getAppDomainHost';

import {
  IncentiveOffer,
  IncentiveOfferOperation,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';

import { setModal } from 'modules/_shared/redux/modalActions';
import { getLinkedTo } from 'modules/documents/components/Modals/UploadDocumentModal/utils/getLinkedTo';
import { addDocument, updateDocument } from 'modules/documents/redux/documentActions';
import { updateBeneficiary } from 'modules/incentivesMarket/api/beneficiaryActions';
import { subcategoryTypes } from 'modules/incentivesMarket/constants/incentiveOfferOperationDocumentTypes';
import {
  createIncentiveOfferOperation,
  deleteIncentiveOfferOperation,
  updateIncentiveOfferOperation,
} from 'modules/incentivesMarket/redux/incentiveOfferOperationActions';
import { collectPublisherAuditContext } from 'modules/incentivesMarket/utils/collectPublisherAuditContext';
import { resolveIncentiveOfferOperationParties } from 'modules/incentivesMarket/utils/resolveIncentiveOfferOperationParties';
import { getIncentiveOfferAcceptTotalPrice } from 'modules/incentivesMarket/utils/getIncentiveOfferAcceptTotalPrice';
import { generateIncentivesMarketPurchaseContract } from 'modules/incentivesMarket/utils/generateIncentivesMarketPurchaseContract';
import { IncentivesMarketPlanContext } from 'modules/incentivesMarket/types/IncentivesMarketBeneficiaryContext';

import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';

import { ScreenSizes } from 'constants/sizes';
import { AcceptIncentiveOfferModalContextType } from '../types/AcceptIncentiveOfferModalContextType';

const defaultContextValue: AcceptIncentiveOfferModalContextType = {
  data: {
    offer: {} as IncentiveOffer,
    beneficiaryBuyer: null,
    purchaseQuantity: 0,
    currentStep: 1,
    isTermsAccepted: false,
    isPurchaseContractAccepted: false,
    isIrrevocableAccepted: false,
    isFiscalAccepted: false,
    isDataVerified: false,
    isLoading: false,
    offerReference: '',
    sellerName: '',
    buyerPlansLabel: '',
    sellerPlans: [],
    sellPlanSelection: null,
    totalPrice: 0,
    buyerDocuments: [],
    bankOwnershipDocuments: [],
    participantSignature: null,
  },
  actions: {
    handleCloseModal: () => {},
    handleNextStep: () => {},
    handlePrevStep: () => {},
    handleToggleTerms: () => {},
    handleTogglePurchaseContract: () => {},
    handleToggleIrrevocable: () => {},
    handleToggleFiscal: () => {},
    handleToggleDataVerified: () => {},
    handleAddDocument: () => {},
    handleRemoveDocument: () => {},
    handleAddBankOwnershipDocument: () => {},
    handleRemoveBankOwnershipDocument: () => {},
    handleUpdatePartnerBuyerField: () => {},
    handleUpdatePartnerBuyerAddressField: () => {},
    setParticipantSignature: () => {},
    handleConfirmPurchase: async () => {},
  },
};

export const AcceptIncentiveOfferModalContext =
  createContext<AcceptIncentiveOfferModalContextType>(defaultContextValue);

type AcceptIncentiveOfferModalProviderProps = {
  children: ReactNode;
  offer: IncentiveOffer;
  beneficiaryBuyer: Partial<IncentivesMarketBeneficiary> | null;
  purchaseQuantity: number;
  societyId: string;
  offerReference: string;
  sellerName: string;
  buyerPlansLabel: string;
  sellerPlans?: IncentivesMarketPlanContext[];
  sellPlanSelection?: Record<string, number> | null;
  onSuccess?: () => void;
  size?: ScreenSizes;
};

export const AcceptIncentiveOfferModalProvider: FC<
  AcceptIncentiveOfferModalProviderProps
> = ({
  children,
  offer,
  beneficiaryBuyer,
  purchaseQuantity,
  societyId,
  offerReference,
  sellerName,
  buyerPlansLabel,
  sellerPlans = [],
  sellPlanSelection = null,
  onSuccess,
}) => {
  const { t } = useTranslate();

  const totalPrice = useMemo(
    () =>
      getIncentiveOfferAcceptTotalPrice({
        offer,
        purchaseQuantity,
        sellerPlans,
        sellPlanSelection,
      }),
    [offer, purchaseQuantity, sellerPlans, sellPlanSelection]
  );
  const user = useSelector((state: { user?: { _id?: string } }) => state.user);

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isPurchaseContractAccepted, setIsPurchaseContractAccepted] =
    useState(false);
  const [isIrrevocableAccepted, setIsIrrevocableAccepted] = useState(false);
  const [isFiscalAccepted, setIsFiscalAccepted] = useState(false);
  const [isDataVerified, setIsDataVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [buyerDocuments, setBuyerDocuments] = useState<File[]>([]);
  const [bankOwnershipDocuments, setBankOwnershipDocuments] = useState<File[]>(
    []
  );
  const [participantSignature, setParticipantSignature] = useState<
    string | null
  >(null);
  const [editablePartnerBuyer, setEditablePartnerBuyer] = useState<
    Partial<IncentivesMarketBeneficiary> | null
  >(
    beneficiaryBuyer
      ? {
          ...beneficiaryBuyer,
          address: beneficiaryBuyer.address
            ? { ...beneficiaryBuyer.address }
            : {
                line1: '',
                zip: '',
                city: '',
                state: '',
                country: '',
              },
          bankAccountNumber: beneficiaryBuyer.bankAccountNumber || '',
        }
      : null
  );

  const validateDocumentFile = (file: File): boolean => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
    ];
    if (file.size > maxSize) {
      errorSwal(t('FileTooLargeMax5MB'));
      return false;
    }
    if (!allowedTypes.includes(file.type)) {
      errorSwal(t('InvalidFileTypePDFJPGPNG'));
      return false;
    }
    return true;
  };

  const handleCloseModal = () => {
    store.dispatch(setModal(null));
  };

  const handleNextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 3));
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleAddDocument = (file: File) => {
    if (!validateDocumentFile(file)) return;
    setBuyerDocuments((prev) => [...prev, file]);
  };

  const handleRemoveDocument = (index: number) => {
    setBuyerDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddBankOwnershipDocument = (file: File) => {
    if (!validateDocumentFile(file)) return;
    setBankOwnershipDocuments((prev) => [...prev, file]);
  };

  const handleRemoveBankOwnershipDocument = (index: number) => {
    setBankOwnershipDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdatePartnerBuyerField = (field: string, value: string) => {
    setEditablePartnerBuyer((prev) =>
      prev ? { ...prev, [field]: value } : null
    );
  };

  const handleUpdatePartnerBuyerAddressField = (field: string, value: string) => {
    setEditablePartnerBuyer((prev) => {
      if (!prev) return null;
      const currentAddress = prev.address || {
        line1: '',
        zip: '',
        city: '',
        state: '',
        country: '',
      };
      return {
        ...prev,
        address: { ...currentAddress, [field]: value },
      };
    });
  };

  const handleConfirmPurchase = async () => {
    const buyerToUse = editablePartnerBuyer || beneficiaryBuyer;
    if (!buyerToUse) return;

    const participantId = buyerToUse._id || beneficiaryBuyer?._id;
    const publisherId =
      typeof offer.beneficiary === 'string' ? offer.beneficiary : offer.beneficiary._id;

    if (!participantId || !publisherId) return;

    const isSellOffer = offer.type === 'SELL';
    const { buyer, seller } = resolveIncentiveOfferOperationParties({
      offerType: offer.type,
      publisherBeneficiaryId: publisherId,
      participantBeneficiaryId: participantId,
    });

    try {
      setIsLoading(true);

      const participantDocumentIds: string[] = [];

      const uploadParticipantDocument = async (
        file: File,
        subcategory: string,
        description: string
      ) => {
        const fileType =
          fileTypes[file.type as keyof typeof fileTypes] || 'PDF';
        const newDocument = {
          file,
          date: new Date(),
          size: file.size,
          name: file.name,
          author: user?._id,
          society: societyId,
          fileType,
          category: documentTypes.DOCUMENTS,
          subcategory,
          description,
        };
        const uploadedDoc = await store.dispatch(addDocument(newDocument));
        if (uploadedDoc?._id) {
          participantDocumentIds.push(uploadedDoc._id);
        }
      };

      for (const file of buyerDocuments) {
        await uploadParticipantDocument(
          file,
          subcategoryTypes.TRADING_OFFER_OPERATION.IDENTITY_DOCUMENT,
          t('IdentityDocumentForIncentiveOffer')
        );
      }

      for (const file of bankOwnershipDocuments) {
        await uploadParticipantDocument(
          file,
          subcategoryTypes.TRADING_OFFER_OPERATION.OWNERSHIP_CERTIFICATE,
          t('BankOwnershipCertificateForIncentiveOffer')
        );
      }

      const participantUpdateData = {
        name: buyerToUse.name,
        cif: buyerToUse.cif,
        email: buyerToUse.email,
        phone: buyerToUse.phone,
        address: buyerToUse.address,
        bankAccountNumber: buyerToUse.bankAccountNumber,
      };

      const signaturePayload: {
        buyerSignature?: string;
        sellerSignature?: string;
      } = {};
      if (participantSignature) {
        if (isSellOffer) {
          signaturePayload.buyerSignature = participantSignature;
        } else {
          signaturePayload.sellerSignature = participantSignature;
        }
      }

      const operationData = {
        society: societyId,
        incentiveOffer: offer._id,
        buyer,
        seller,
        unitClass:
          typeof offer.unitClass === 'string'
            ? offer.unitClass
            : offer.unitClass?._id,
        unitsCount: purchaseQuantity,
        unitPrice: offer.unitPrice,
        ...signaturePayload,
        ...(isSellOffer
          ? {
              buyerDocuments: participantDocumentIds,
              buyerUpdateData: participantUpdateData,
            }
          : {
              sellerDocuments: participantDocumentIds,
            }),
        participantAuditContext: collectPublisherAuditContext(),
        domain: getAppDomainHost(),
      };

      const createdOp = (await store.dispatch(
        createIncentiveOfferOperation(operationData)
      )) as IncentiveOfferOperation | undefined;

      if (!createdOp?._id) {
        throw new Error('OPERATION_CREATE_FAILED');
      }

      if (!isSellOffer && participantId) {
        await store.dispatch(
          updateBeneficiary({
            beneficiaryId: participantId,
            updateBeneficiaryData: {
              ...participantUpdateData,
              userId: user?._id || '',
              societyId,
            },
          })
        );
      }

      if (createdOp._id && participantDocumentIds.length > 0) {
        await Promise.all(
          participantDocumentIds.map((docId) =>
            store.dispatch(
              updateDocument(
                {
                  _id: docId,
                  linkedTo: getLinkedTo({
                    incentiveOfferOperationId: createdOp._id,
                  }),
                },
                false
              )
            )
          )
        );
      }

      const actualSociety = store.getState().society?.actualSociety;

      try {
        const contractDoc = await generateIncentivesMarketPurchaseContract({
          dispatch: store.dispatch,
          operation: createdOp,
          societyId,
          society: actualSociety,
          userId: user?._id,
          t,
        });

        await store.dispatch(
          updateIncentiveOfferOperation(createdOp._id, {
            purchaseContractDocumentId: contractDoc.documentId,
            buyerId: buyer,
            sellerId: seller,
          } as any)
        );
      } catch (contractError) {
        await store.dispatch(deleteIncentiveOfferOperation(createdOp._id));

        if (
          contractError instanceof Error &&
          contractError.message === 'NO_INCENTIVES_MARKET_TEMPLATE'
        ) {
          errorSwal(t('ErrorNoIncentivesMarketTemplate'));
        } else {
          errorSwal(t('ErrorGeneratingContract'));
        }
        return;
      }

      setIsLoading(false);
      successSwal(t('PurchaseConfirmedSuccessfully'));
      handleCloseModal();

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      setIsLoading(false);
      errorSwal(t('ErrorConfirmingPurchase'));
    }
  };

  return (
    <AcceptIncentiveOfferModalContext.Provider
      value={{
        data: {
          offer,
          beneficiaryBuyer: editablePartnerBuyer ?? beneficiaryBuyer,
          purchaseQuantity,
          currentStep,
          isTermsAccepted,
          isPurchaseContractAccepted,
          isIrrevocableAccepted,
          isFiscalAccepted,
          isDataVerified,
          isLoading,
          offerReference,
          sellerName,
          buyerPlansLabel,
          sellerPlans,
          sellPlanSelection,
          totalPrice,
          buyerDocuments,
          bankOwnershipDocuments,
          participantSignature,
        },
        actions: {
          handleCloseModal,
          handleNextStep,
          handlePrevStep,
          handleToggleTerms: () => setIsTermsAccepted((prev) => !prev),
          handleTogglePurchaseContract: () =>
            setIsPurchaseContractAccepted((prev) => !prev),
          handleToggleIrrevocable: () =>
            setIsIrrevocableAccepted((prev) => !prev),
          handleToggleFiscal: () => setIsFiscalAccepted((prev) => !prev),
          handleToggleDataVerified: () => setIsDataVerified((prev) => !prev),
          handleAddDocument,
          handleRemoveDocument,
          handleAddBankOwnershipDocument,
          handleRemoveBankOwnershipDocument,
          handleUpdatePartnerBuyerField,
          handleUpdatePartnerBuyerAddressField,
          setParticipantSignature,
          handleConfirmPurchase,
        },
      }}
    >
      {children}
    </AcceptIncentiveOfferModalContext.Provider>
  );
};

export const useAcceptIncentiveOfferModal = () => {
  const context = useContext(AcceptIncentiveOfferModalContext);
  if (!context) {
    throw new Error(
      'useAcceptIncentiveOfferModal must be used within a AcceptIncentiveOfferModalProvider'
    );
  }
  return context;
};
